import React, { useMemo } from 'react';
import { BaseSelector, BaseSelectorProps } from '../BaseSelector';
import { Option } from '../CustomDropdown';
import { FLESCH_READABILITY_LIST } from './FleschReadability';
import { useFleschReadabilityRefinement } from './useFleschReadabilityRefinement';
import CustomOptionWithCount from '../CustomOptionWithCount';

interface FleschReadabilityWithCountsProps
  extends Omit<BaseSelectorProps, 'label' | 'options'> {
  // If true, will attempt to use Algolia refinement context for counts
  enableCounts?: boolean;
  // Manual override for count data (useful for testing or custom data sources)
  countData?: Array<{ value: string; count: number }>;
}

/**
 * Enhanced FleschReadability selector that displays counts from Algolia refinements
 */
export const FleschReadabilityWithCounts: React.FC<FleschReadabilityWithCountsProps> = ({
  enableCounts = false,
  countData,
  ...props
}) => {
  // Always call the hook at top level, but only use it if enableCounts is true
  const fleschReadabilityRefinement = useFleschReadabilityRefinement();

  const optionsWithCounts = useMemo((): Option[] => {
    // Generate base options from the readability list
    const baseOptions = FLESCH_READABILITY_LIST.map((readability) => ({
      label: readability,
      value: readability,
    }));

    // If counts are not enabled, return base options
    if (!enableCounts) {
      return baseOptions;
    }

    // Add counts from manual data if provided
    if (countData) {
      return baseOptions.map((option) => {
        const countItem = countData.find((item) => item.value === option.value);
        return {
          ...option,
          count: countItem?.count,
        };
      });
    }

    // Add counts from refinement context if available
    if (fleschReadabilityRefinement && enableCounts) {
      return baseOptions.map((option) => {
        const count = fleschReadabilityRefinement.getCountForValue(option.value);
        return {
          ...option,
          count: count > 0 ? count : 0,
        };
      });
    }

    // Fallback to base options if no count data available
    return baseOptions;
  }, [enableCounts, countData, fleschReadabilityRefinement]);

  // Custom label functions to show counts when available
  const optionLabel = (option: Option) => {
    if (enableCounts && option.count !== undefined) {
      return <CustomOptionWithCount value="" label={option.label} count={option.count} />;
    }
    return option.label;
  };

  const selectedLabel = (selected: Option) => `Flesch Readability: ${selected.label}`;

  return (
    <BaseSelector
      {...props}
      label="Flesch Readability"
      options={optionsWithCounts}
      optionLabel={optionLabel}
      selectedLabel={selectedLabel}
      tooltip="Flesch Reading Ease Score - indicates how easy text is to read"
    />
  );
};

export default FleschReadabilityWithCounts;
